/* eslint-disable max-len */
import moment from 'moment';

import type {
  GoalScopeFormValues,
  PCFRecordData
} from '../../../services/interface/pcfTypes';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import {PCF_STUDY_DATE_FORMAT} from '../utils/goalScopeValidation';
import {getDefaultReferenceYear} from '../utils/referenceYearUtils';

import {lciFormInitialValues} from './lciFormValues';
import {ALLOCATION_METHODS, PCF_STANDARDS} from './pcfOptions';

type StageId =
  | 'A1'
  | 'A2'
  | 'A3'
  | 'A4'
  | 'A5'
  | 'B1'
  | 'B2'
  | 'B3'
  | 'B4'
  | 'B5'
  | 'B6'
  | 'B7'
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'D';

const goalScopeStagesInitial: Partial<Record<StageId, boolean>> = {
  A1: true,
  A2: true,
  A3: true
};

const GOAL_SCOPE_STATIC_VALUES: Omit<
  GoalScopeFormValues,
  'reportingOrg' | 'studyDate' | 'referenceYear'
> = {
  studyName: '',
  studyDescription: '',
  productName: '',
  productCode: '',
  industrySector: [],
  industryOther: '',
  hsnCode: [],
  intendedApplication: undefined,
  productCNCode: '',
  pcfStandard: PCF_STANDARDS[0]?.value ?? '',
  audienceSelection: ['internal'],
  audienceOther: '',
  fuQuantity: 1,
  fuUnit: 'kg',
  fuUnitOther: '',
  fuDescription:
    'One injection-moulded polypropylene bracket providing structural support in a passenger vehicle door assembly over a 10-year service life.',
  referenceFlow:
    '1.05 kg of polypropylene granules (to account for 5% manufacturing waste).',
  boundaryScope: 'cradle_to_gate',
  stages: goalScopeStagesInitial,
  exclusions: '',
  geographicScope: 'india_national',
  allocationMethod: ALLOCATION_METHODS[0]?.value ?? '',
  expertMode: false,
  _stageGate: true
};

export const getGoalScopeInitialValues = (): GoalScopeFormValues => {
  const studyDate = moment().format(PCF_STUDY_DATE_FORMAT);

  return {
    ...GOAL_SCOPE_STATIC_VALUES,
    reportingOrg: useCompanyStore.getState().companyDetails?.companyName ?? '',
    studyDate,
    referenceYear: getDefaultReferenceYear(studyDate)
  };
};

/** @deprecated Prefer getGoalScopeInitialValues() for fresh defaults */
export const GOAL_SCOPE_INITIAL_VALUES: GoalScopeFormValues =
  getGoalScopeInitialValues();

export const impactInitialValues = {
  lciaMethod: 'ipcc_2021_gwp100',
  gwpHorizon: 'gwp100',
  biogenicHandling: 'exclude_biogenic',
  luc: false,
  sequestration: false,
  uncertainty: false,
  uncertaintyIterations: 1000
};

export const resultsInterpretationInitialValues = {
  keyFindings:
    'The dominant emissions come from polypropylene input production and grid electricity use. Reduction opportunities include recycled PP content, renewable electricity sourcing, and transport optimization.',
  limitations: 'Prototype study for design and development alignment.',
  benchmarkValue: '12.5',
  benchmarkLabel: ''
};

export const reportInitialValues = {
  reportType: undefined as string | undefined,
  reportLanguage: 'english',
  verificationStatus: undefined as string | undefined,
  signatoryName: 'Demo Signatory',
  signatoryDesignation: 'Sustainability Lead',
  thirdPartyVerification: undefined as string | undefined,
  includeInventory: false,
  shareStudy: undefined as string | undefined,
  formats: {
    pdf: true,
    csv: false,
    json: false,
    excel: false,
    ilcd: false
  }
};

export const buildDefaultRecordData = (): PCFRecordData => ({
  goalAndScope: getGoalScopeInitialValues(),
  lci: lciFormInitialValues,
  impactConfiguration: impactInitialValues,
  results: undefined,
  report: undefined
});
